export const RELAYED_CONSENT_SIGNALS = [
  'analytics_storage',
  'ad_storage',
  'ad_user_data',
  'ad_personalization'
] as const;

const CONSENT_POLL_INTERVAL_MS = 1500;

export type ConsentSignal = (typeof RELAYED_CONSENT_SIGNALS)[number];
export type ConsentSignalValue = 'granted' | 'denied';
export type ConsentSignals = Partial<Record<ConsentSignal, ConsentSignalValue>>;

export interface HostConsentDecision {
  signals: ConsentSignals;
  decidedAt: number;
}

interface ConsentCommand {
  mode: 'default' | 'update';
  state: Record<string, unknown>;
}

function isConsentSignalValue(value: unknown): value is ConsentSignalValue {
  return value === 'granted' || value === 'denied';
}

function readConsentCommand(entry: unknown): ConsentCommand | null {
  if (typeof entry !== 'object' || entry === null) return null;

  const [command, mode, state] = Array.from(entry as ArrayLike<unknown>);
  if (command !== 'consent') return null;
  if (mode !== 'default' && mode !== 'update') return null;
  if (typeof state !== 'object' || state === null) return null;

  return { mode, state: state as Record<string, unknown> };
}

function readConsentFromDataLayer(): ConsentSignals | null {
  if (!Array.isArray(window.dataLayer)) return null;

  const defaultSignals: ConsentSignals = {};
  const updatedSignals: ConsentSignals = {};
  let guestHasChosen = false;
  for (const entry of window.dataLayer) {
    const command = readConsentCommand(entry);
    if (!command) continue;

    let commandHasRelayedSignal = false;
    for (const signal of RELAYED_CONSENT_SIGNALS) {
      const value = command.state[signal];
      if (isConsentSignalValue(value)) {
        const signals = command.mode === 'update' ? updatedSignals : defaultSignals;
        signals[signal] = value;
        commandHasRelayedSignal = true;
      }
    }
    if (command.mode === 'update' && commandHasRelayedSignal) guestHasChosen = true;
  }

  const signals = { ...defaultSignals, ...updatedSignals };
  return guestHasChosen && Object.keys(signals).length > 0 ? signals : null;
}

function getGoogleTagDataConsentEntries(): Record<string, unknown> | null {
  const ics = window.google_tag_data?.ics;
  if (typeof ics !== 'object' || ics === null) return null;

  const entries = (ics as { entries?: unknown }).entries;
  if (typeof entries !== 'object' || entries === null) return null;

  return entries as Record<string, unknown>;
}

function readConsentFromGoogleTagData(): ConsentSignals | null {
  try {
    const entries = getGoogleTagDataConsentEntries();
    if (!entries) return null;

    const signals: ConsentSignals = {};
    let guestHasChosen = false;
    for (const signal of RELAYED_CONSENT_SIGNALS) {
      const entry = entries[signal];
      if (typeof entry !== 'object' || entry === null) continue;

      const updatedValue = (entry as { update?: unknown }).update;
      if (typeof updatedValue === 'boolean') {
        guestHasChosen = true;
        signals[signal] = updatedValue ? 'granted' : 'denied';
      }
    }

    return guestHasChosen && Object.keys(signals).length > 0 ? signals : null;
  } catch {
    return null;
  }
}

export function getRelayableConsent(): HostConsentDecision | null {
  const dataLayerSignals = readConsentFromDataLayer();
  const googleTagDataSignals = readConsentFromGoogleTagData();
  if (!dataLayerSignals && !googleTagDataSignals) return null;

  return {
    signals: { ...(dataLayerSignals || {}), ...(googleTagDataSignals || {}) },
    decidedAt: Date.now()
  };
}

export function encodeConsentToken(decision: HostConsentDecision): string {
  return btoa(JSON.stringify({ ...decision.signals, t: decision.decidedAt }))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function consentSignalsSignature(signals: ConsentSignals): string {
  return RELAYED_CONSENT_SIGNALS.map(signal => `${signal}=${signals[signal] ?? ''}`).join('&');
}

export function consentDecisionSignature(decision: HostConsentDecision | null): string | null {
  return decision ? consentSignalsSignature(decision.signals) : null;
}

export function consentTokenSignature(token: string): string | null {
  try {
    const base64 = token.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4)));
    if (typeof payload !== 'object' || payload === null) return null;

    const signals: ConsentSignals = {};
    for (const signal of RELAYED_CONSENT_SIGNALS) {
      const value = payload[signal];
      if (isConsentSignalValue(value)) signals[signal] = value;
    }
    return Object.keys(signals).length > 0 ? consentSignalsSignature(signals) : null;
  } catch {
    return null;
  }
}

export function subscribeToConsentUpdates(
  callback: (decision: HostConsentDecision, signature: string) => void,
  options: { pollIntervalMs?: number; initialSignature?: string | null } = {}
): () => void {
  let lastSignature =
    options.initialSignature !== undefined
      ? options.initialSignature
      : consentDecisionSignature(getRelayableConsent());

  function relayIfChanged(): void {
    try {
      const decision = getRelayableConsent();
      if (!decision) return;
      const signature = consentSignalsSignature(decision.signals);
      if (signature === lastSignature) return;
      lastSignature = signature;
      callback(decision, signature);
    } catch {}
  }

  relayIfChanged();
  const pollId = setInterval(relayIfChanged, options.pollIntervalMs ?? CONSENT_POLL_INTERVAL_MS);
  return () => clearInterval(pollId);
}
